import { HttpRequestEntity } from 'shared/domain/httpRequest/httpRequestModel';
import { ProjectInDto } from 'shared/domain/project/types/dto';
import { broadcastUploadedProjectError } from 'serviceWorker/broadcasts/project/error';
import { broadcastAllProjects } from 'serviceWorker/broadcasts/project/all';
import {
  broadcastCreatedProject,
  broadcastDeletedProject,
  broadcastRestoredProject,
  broadcastUpdatedProject,
} from 'serviceWorker/broadcasts/project/one';
import { neverReached } from 'shared/utils/neverReached';
import { chooseErrorMessage, isErrorResponsePayload } from 'shared/utils/errorHandling';

export function broadcastProjectResponse(
  request: HttpRequestEntity,
  response: ProjectInDto | unknown
): void {
  const { uniqueId } = request.data;
  if (isErrorResponsePayload(response)) {
    return broadcastUploadedProjectError(response.error, chooseErrorMessage(response.error), uniqueId);
  }
  switch (request.method) {
    case 'POST':
      // Never rejects: a failure is broadcast as `failUploadedEntity` with the `uniqueId`.
      return void broadcastCreatedProject(response as ProjectInDto, uniqueId);
    case 'PUT':
      // The list follows, so the selected project's metadata is refreshed too.
      return void broadcastUpdatedProject(response as ProjectInDto, uniqueId).then(
        async (stored) => {
          if (stored) await broadcastAllProjects();
        }
      );
    // A restore is queued as GET; its runner answers `true`.
    case 'GET':
      return broadcastRestoredProject(response, uniqueId);
    case 'DELETE':
      return broadcastDeletedProject(response, uniqueId);
    default:
      neverReached(request.method);
  }
}

/** Carries the change's `uniqueId`, so the page's command rejects with this error. */
export function broadcastProjectError(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): void {
  broadcastUploadedProjectError(error, chooseErrorMessage(error), uniqueId ?? request.data?.uniqueId);
}
